import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PrinterIcon, ArrowLeftIcon } from "lucide-react";

/**
 * Romaneio de Recebimento — página imprimível.
 * URL: /romaneio/recebimento/:remessa
 */
export default function RomaneioRecebimento() {
  const { remessa } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/romaneio/recebimento/${encodeURIComponent(remessa)}`);
        setData(data);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      }
    })();
  }, [remessa]);

  const now = new Date();
  if (error) return <div className="min-h-screen bg-white text-black flex items-center justify-center">
    <div className="text-center">
      <div className="text-red-600 font-bold text-lg mb-2">Erro ao carregar romaneio</div>
      <div className="text-sm text-gray-600">{error}</div>
    </div>
  </div>;
  if (!data) return <div className="min-h-screen bg-white text-black flex items-center justify-center">Carregando romaneio...</div>;

  return (
    <div className="bg-white text-black min-h-screen">
      <style>{`
        @media print { .no-print { display: none !important; } @page { size: A4; margin: 1cm; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        @media screen { body { background: #f0f0f0; } }
      `}</style>
      <div className="no-print bg-[#0A0A0C] px-6 py-3 flex items-center justify-between text-white sticky top-0 z-10">
        <Link to="/recebimento" className="text-xs uppercase tracking-widest text-[#00E5FF] hover:underline flex items-center gap-1">
          <ArrowLeftIcon className="w-3 h-3" /> Voltar
        </Link>
        <button onClick={() => window.print()} data-testid="romaneio-print" className="bg-[#FFD60A] text-black font-bold uppercase tracking-wider text-xs px-6 py-2 hover:bg-[#FFD60A]/85 flex items-center gap-2">
          <PrinterIcon className="w-4 h-4" /> Imprimir / PDF
        </button>
      </div>

      <div className="max-w-[210mm] mx-auto bg-white p-8 shadow-lg my-4" data-testid="romaneio-rec-container">
        <div className="flex items-start justify-between border-b-4 border-black pb-4 mb-6">
          <div>
            <div className="text-xs tracking-widest text-gray-600">CHEM·WMS · Envase Químico</div>
            <div className="text-3xl font-bold mt-1">ROMANEIO DE RECEBIMENTO</div>
            <div className="text-xs text-gray-600 mt-1">Recebimento de matéria-prima a granel · Emissão: {now.toLocaleString("pt-BR")}</div>
          </div>
          <div className="text-right">
            <div className="text-4xl font-black">#{data.remessa}</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-600 mt-1">Nº da Remessa</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="border border-black p-3">
            <div className="text-[9px] uppercase tracking-wider text-gray-600">Fornecedor</div>
            <div className="font-bold text-lg">{data.fornecedor || "—"}</div>
            <div className="text-xs mt-1">Lote Fabricante: {data.lote_fabricante || "—"}</div>
          </div>
          <div className="border border-black p-3">
            <div className="text-[9px] uppercase tracking-wider text-gray-600">Nota Fiscal</div>
            <div className="font-bold text-lg">{data.nf_recebimento || "—"}</div>
            <div className="text-xs mt-1">Data: {data.data_hora ? new Date(data.data_hora).toLocaleString("pt-BR") : "—"}</div>
          </div>
        </div>

        <div className="border-2 border-black p-4 mb-6">
          <div className="text-[9px] uppercase tracking-wider text-gray-600 mb-1">Produto Químico</div>
          <div className="text-2xl font-bold">{data.nome_produto}</div>
          <div className="text-sm text-gray-700 mt-1">Código: <span className="font-mono">{data.codigo_produto}</span> · Função: <span className="font-mono">{data.funcao_quimica}</span></div>
        </div>

        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="border border-black p-3">
            <div className="text-[9px] uppercase tracking-wider text-gray-600">Peso Bruto</div>
            <div className="font-mono font-bold text-xl">{Number(data.peso_bruto || 0).toLocaleString("pt-BR")} kg</div>
          </div>
          <div className="border border-black p-3">
            <div className="text-[9px] uppercase tracking-wider text-gray-600">Densidade</div>
            <div className="font-mono font-bold text-xl">{data.densidade} kg/L</div>
          </div>
          <div className="border border-black p-3">
            <div className="text-[9px] uppercase tracking-wider text-gray-600">Volume Teórico</div>
            <div className="font-mono font-bold text-xl">{Number(data.volume_calculado || 0).toLocaleString("pt-BR")} L</div>
          </div>
          <div className="border border-black p-3">
            <div className="text-[9px] uppercase tracking-wider text-gray-600">Envasado</div>
            <div className="font-mono font-bold text-xl">{Number(data.volume_envasado || 0).toLocaleString("pt-BR")} L</div>
          </div>
        </div>

        <div className="border-t border-black pt-2 mb-8">
          <div className="text-xs">
            <span className="text-gray-600">Status atual:</span>{" "}
            <span className="font-bold uppercase">{(data.status || "").replace(/_/g, " ")}</span>
          </div>
          <div className="text-xs mt-1">
            <span className="text-gray-600">Operador de recebimento:</span> <span className="font-bold">{data.usuario_recebimento || "—"}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mt-16">
          <div className="text-center">
            <div className="border-t-2 border-black pt-1 text-xs">Assinatura do Transportador</div>
          </div>
          <div className="text-center">
            <div className="border-t-2 border-black pt-1 text-xs">Conferido / Recebido por</div>
          </div>
        </div>

        <div className="text-[9px] text-center text-gray-500 mt-12 border-t border-gray-300 pt-2">
          Documento gerado eletronicamente pelo CHEM·WMS · {now.toISOString()}
        </div>
      </div>
    </div>
  );
}
